/**
 * getCaretLeft — the horizontal pixel offset of the character at `index`
 * within a textarea's content box, used to anchor the @-mention popup over
 * the "@" column.
 *
 * A textarea exposes no per-character geometry, so mirror it: build a hidden
 * <div> that copies the font/padding/border/width metrics that drive glyph
 * advances, fill it with the text up to `index`, place a marker span there,
 * and read the marker's offsetLeft (minus the textarea's horizontal scroll).
 * The div uses `white-space: pre-wrap` so wrapping matches the textarea.
 *
 * jsdom has no layout engine, so this returns 0 there — callers clamp/fallback
 * to a left-aligned card; verify real positioning in the add-in host.
 */

// Style properties that affect where a glyph lands. Kept minimal but complete
// enough that the mirror's line-breaking and advances track the textarea.
const MIRRORED_PROPS = [
  "boxSizing",
  "width",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "textTransform",
  "wordSpacing",
  "lineHeight",
  "tabSize",
] as const;

export function getCaretLeft(textarea: HTMLTextAreaElement, index: number): number {
  const doc = textarea.ownerDocument;
  const win = doc.defaultView;
  if (!win) return 0;
  const computed = win.getComputedStyle(textarea);

  const mirror = doc.createElement("div");
  const style = mirror.style;
  style.position = "absolute";
  style.visibility = "hidden";
  style.whiteSpace = "pre-wrap";
  style.wordWrap = "break-word";
  style.overflow = "hidden";
  style.top = "0";
  style.left = "-9999px";
  for (const prop of MIRRORED_PROPS) {
    style[prop] = computed[prop];
  }

  const clamped = Math.max(0, Math.min(index, textarea.value.length));
  mirror.textContent = textarea.value.slice(0, clamped);
  const marker = doc.createElement("span");
  // A non-empty marker so it has a box even at end-of-text.
  marker.textContent = "​";
  mirror.appendChild(marker);

  doc.body.appendChild(mirror);
  const left = marker.offsetLeft - textarea.scrollLeft;
  doc.body.removeChild(mirror);

  return Number.isFinite(left) ? left : 0;
}
